import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Autocomplete,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Vocabulary } from "../../Utils/Vocabulary";
import { ETicketPriority, ETicketType } from "../../Utils/Enums";
import { createSupportTicket, updateSupportTicket } from "../../Services/supportTicketsService";
import { getData } from "../../Services/getData";
import { endpoints } from "../../Utils/UrlEnum";

interface TicketFormModalProps {
  open: boolean;
  onClose: () => void;
  ticket?: any;
  onSuccess: () => void;
}

const ticketValidationSchema = yup.object().shape({
  title: yup.string().required(Vocabulary.requiredField),
  description: yup.string().required(Vocabulary.requiredField),
  priority: yup.string().required(Vocabulary.requiredField),
  type: yup.string().required(Vocabulary.requiredField),
  assignedTo: yup.array().nullable(),
  clientId: yup.string().nullable(),
  tags: yup.array().nullable(),
});

const TicketFormModal: React.FC<TicketFormModalProps> = ({ open, onClose, ticket, onSuccess }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(ticketValidationSchema),
    defaultValues: {
      title: "",
      description: "",
      priority: ETicketPriority.MEDIUM,
      type: ETicketType.SUPPORT,
      assignedTo: [],
      clientId: null,
      tags: [],
    },
  });

  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchClients();
      if (ticket) {
        reset({
          title: ticket.title || "",
          description: ticket.description || "",
          priority: ticket.priority || ETicketPriority.MEDIUM,
          type: ticket.type || ETicketType.SUPPORT,
          assignedTo: ticket.assignedTo?.map((u: any) => u._id) || [],
          clientId: ticket.clientId?._id || null,
          tags: ticket.tags || [],
        });
      }
    }
  }, [open, ticket, reset]);

  const fetchUsers = async () => {
    setLoadingData(true);
    try {
      const response: any = await getData(endpoints.user.main);
      if (response?.data) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error(Vocabulary.errorGettingUsers, error);
    } finally {
      setLoadingData(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response: any = await getData(endpoints.client.main);
      if (response?.data) {
        setClients(response.data);
      }
    } catch (error) {
      console.error(Vocabulary.errorGettingClients, error);
    }
  };

  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      if (ticket?._id) {
        await updateSupportTicket(ticket._id, data);
      } else {
        await createSupportTicket(data);
      }
      onSuccess();
      handleClose();
    } catch (error) {
      console.error("Error saving ticket:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {ticket ? Vocabulary.edit : Vocabulary.createTicket}
      </DialogTitle>
      <DialogContent>
        {loadingData ? (
          <CircularProgress />
        ) : (
          <form id="ticket-form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={Vocabulary.ticketTitle}
                      error={!!errors.title}
                      helperText={errors.title?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={4}
                      label={Vocabulary.ticketDescription}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label={Vocabulary.ticketPriority}
                      error={!!errors.priority}
                      helperText={errors.priority?.message}
                    >
                      <MenuItem value={ETicketPriority.LOW}>{Vocabulary.priorityLow}</MenuItem>
                      <MenuItem value={ETicketPriority.MEDIUM}>{Vocabulary.priorityMedium}</MenuItem>
                      <MenuItem value={ETicketPriority.HIGH}>{Vocabulary.priorityHigh}</MenuItem>
                      <MenuItem value={ETicketPriority.URGENT}>{Vocabulary.priorityUrgent}</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label={Vocabulary.ticketType}
                      error={!!errors.type}
                      helperText={errors.type?.message}
                    >
                      <MenuItem value={ETicketType.BUG}>{Vocabulary.typeBug}</MenuItem>
                      <MenuItem value={ETicketType.FEATURE_REQUEST}>{Vocabulary.typeFeatureRequest}</MenuItem>
                      <MenuItem value={ETicketType.SUPPORT}>{Vocabulary.typeSupport}</MenuItem>
                      <MenuItem value={ETicketType.QUESTION}>{Vocabulary.typeQuestion}</MenuItem>
                      <MenuItem value={ETicketType.OTHER}>{Vocabulary.typeOther}</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="assignedTo"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={users}
                      getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                      value={users.filter((u) => field.value?.includes(u._id))}
                      onChange={(_, newValue) => {
                        field.onChange(newValue.map((v) => v._id));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={Vocabulary.assignedTo}
                          error={!!errors.assignedTo}
                          helperText={errors.assignedTo?.message as string}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={`${option.firstName} ${option.lastName}`}
                            {...getTagProps({ index })}
                            key={option._id}
                          />
                        ))
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="clientId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={clients}
                      getOptionLabel={(option) => option.name || option.companyName || ""}
                      value={clients.find((c) => c._id === field.value) || null}
                      onChange={(_, newValue) => {
                        field.onChange(newValue?._id || null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={Vocabulary.client}
                          error={!!errors.clientId}
                          helperText={errors.clientId?.message as string}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={field.value || []}
                      onChange={(_, newValue) => {
                        field.onChange(newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={Vocabulary.ticketTags}
                          placeholder={Vocabulary.add}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option}
                            {...getTagProps({ index })}
                            key={index}
                          />
                        ))
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {Vocabulary.cancel}
        </Button>
        <Button
          type="submit"
          form="ticket-form"
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : Vocabulary.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketFormModal;
